"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Button } from "@beweegbaas/ui/components/ui/button";
import { Input } from "@beweegbaas/ui/components/ui/input";
import { Textarea } from "@beweegbaas/ui/components/ui/textarea";
import { cn } from "@beweegbaas/ui/lib/utils";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        [
          // Layout
          "group/input-group relative flex h-9 w-full min-w-0 items-center",
          // Shape & surface
          `squircle border-input
          dark:bg-input/30
          rounded-md border bg-white bg-clip-padding shadow-xs supports-squircle:rounded-full
          supports-squircle:has-[textarea]:rounded-2xl`,
          // Interaction
          "transition-[color,box-shadow] outline-none",
          // Focus (skipped for read-only)
          `has-[[data-slot=input-group-control]:focus-visible:not(:read-only)]:border-ring
          has-[[data-slot=input-group-control]:focus-visible:not(:read-only)]:ring-ring/50
          has-[[data-slot=input-group-control]:focus-visible:not(:read-only)]:ring-[3px]`,
          // Disabled
          "has-[[data-slot=input-group-control]:disabled]:bg-muted",
          // Read only
          "has-[[data-slot=input-group-control]:read-only]:bg-muted",
          // Invalid
          `has-[[data-slot][aria-invalid=true]]:border-destructive
          has-[[data-slot][aria-invalid=true]]:ring-destructive/20
          dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40
          has-[[data-slot][aria-invalid=true]]:ring-[3px]`,
          // Block alignment (column layout)
          `has-[>[data-align=block-end]]:h-auto
          has-[>[data-align=block-end]]:flex-col
          has-[>[data-align=block-start]]:h-auto
          has-[>[data-align=block-start]]:flex-col
          has-[>textarea]:h-auto`,
          // Control padding based on addon alignment
          `has-[>[data-align=block-end]]:[&>input]:pt-3
          has-[>[data-align=block-start]]:[&>input]:pb-3
          has-[>[data-align=inline-end]]:[&>input]:pr-1.5
          has-[>[data-align=inline-start]]:[&>input]:pl-1.5`,
          // Combobox content context
          `[[data-slot=combobox-content]_&]:focus-within:border-inherit
          [[data-slot=combobox-content]_&]:focus-within:ring-0`,
        ],
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  [
    // Layout
    `flex h-auto shrink-0 cursor-text items-center
    justify-center gap-2 select-none`,
    // Typography
    "py-1.5 text-sm font-medium text-muted-foreground",
    // Disabled
    "group-data-[disabled=true]/input-group:opacity-50",
    // Nested elements
    `[&>kbd]:rounded-[calc(var(--radius)-5px)]
    [&>svg:not([class*='size-'])]:size-4`,
  ],
  {
    variants: {
      align: {
        "inline-start": [
          // Layout
          "order-first pl-2",
          // Nested spacing
          `has-[>button]:ml-[-0.25rem]
          has-[>kbd]:ml-[-0.15rem]`,
        ],
        "inline-end": [
          // Layout
          "order-last pr-2",
          // Nested spacing
          `has-[>button]:mr-[-0.25rem]
          has-[>kbd]:mr-[-0.15rem]`,
        ],
        "block-start": [
          // Layout
          "order-first w-full justify-start px-2.5 pt-2",
          // Nested spacing
          `group-has-[>input]/input-group:pt-2
          [.border-b]:pb-2`,
        ],
        "block-end": [
          // Layout
          "order-last w-full justify-start px-2.5 pb-2",
          // Nested spacing
          `group-has-[>input]/input-group:pb-2
          [.border-t]:pt-2`,
        ],
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva(
  [
    // Layout
    "flex items-center gap-2",
    // Typography & surface
    "text-sm shadow-none",
  ],
  {
    variants: {
      size: {
        xs: [
          // Layout
          "h-6 gap-1 px-1.5",
          // Shape
          "rounded-[calc(var(--radius)-5px)]",
          // Icons
          "[&>svg:not([class*='size-'])]:size-3.5",
        ],
        sm: "",
        "icon-xs": [
          // Layout
          "size-6 p-0 has-[>svg]:p-0",
          // Shape
          "rounded-[calc(var(--radius)-5px)]",
        ],
        "icon-sm": [
          // Layout
          "size-8 p-0 has-[>svg]:p-0",
        ],
      },
    },
    defaultVariants: {
      size: "xs",
    },
  },
);

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size" | "type"> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: "button" | "submit" | "reset";
  }) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        [
          // Layout
          "flex items-center gap-2",
          // Typography
          "text-muted-foreground text-sm",
          // Icons
          `[&_svg]:pointer-events-none
          [&_svg:not([class*='size-'])]:size-4`,
        ],
        className,
      )}
      {...props}
    />
  );
}

function InputGroupInput({ className, ...props }: React.ComponentProps<"input">) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        [
          // Layout
          "flex-1",
          // Reset standalone input chrome (group owns border/focus)
          `rounded-none border-0 bg-transparent shadow-none ring-0
          dark:bg-transparent`,
          // Focus / invalid (handled by group)
          "focus-visible:ring-0 aria-invalid:ring-0",
        ],
        className,
      )}
      {...props}
    />
  );
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        [
          // Layout
          "flex-1 resize-none py-2",
          // Reset standalone textarea chrome (group owns border/focus)
          `rounded-none border-0 bg-transparent shadow-none ring-0
          dark:bg-transparent`,
          // Focus / invalid (handled by group)
          "focus-visible:ring-0 aria-invalid:ring-0",
        ],
        className,
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
